"use client";

import { useState, useEffect } from "react";
import styles from "./BuyCarCard.module.scss";
import Text from "@components/atoms/Text";
import Image from "next/image";
import CarGalleryModal from "@components/atoms/CarGalleryModal";
import assetLoader from "@utils/internalImageLoder";
import { BuyCarCardProps } from "./BuyCarCard.types";
import { BUYCARCARD_CONFIG } from "./BuyCarCard.data";
import CardContent from "./components/CardContent";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getCarAvailability } from "@utils/carAvailability";
import { PAGE_TYPES } from "@constants/pageTypes";
import CollectionTrustMarkerBadge from "@components/atoms/CollectionTrustMarkerBadge";
import { getHighestPriorityTrustMarkerCollection } from "@helpers/trustMarkerHelper";
import { isCurrentPageTrustMarkerCollection } from "@helpers/trustMarkerSlugHelper";

const BuyCarCard = ({
  brand_name,
  model_name,
  model_slug,
  variant_count,
  min_price,
  max_price,
  min_acko_drive_savings,
  min_on_road_price,
  min_waiting_period,
  min_emi_amount,
  experts_rating,
  status,
  fuel_types,
  transmission_types,
  isLoggedIn,
  isWishlisted = false,
  cityName,
  collectionName,
  expected_date,
  onWishlistClick,
  onNavigate,
  gallery,
  handleSpinnerLoading,
  title,
  trust_markers,
  ...restProps
}: BuyCarCardProps) => {
  const [pageName, setPageName] = useState<string>("");

  useEffect(() => {
    // Safely access window object on client side
    if (typeof window !== "undefined") {
      const pageLink = window.location.href;
      const pageNameValue = pageLink.includes("collection")
        ? PAGE_TYPES.COLLECTION_PAGE
        : PAGE_TYPES.BUY_HOMEPAGE;
      setPageName(pageNameValue);
    }
  }, []);

  const handleRouteClick = async () => {
    if (onNavigate) {
      handleSpinnerLoading?.(true);
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageName,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_model",
          car_availability: getCarAvailability(status),
          vehicle_make: brand_name,
          vehicle_model: model_name,
          city_name: cityName,
          collection_name: collectionName,
        },
      });
      onNavigate(model_slug);
    }
  };

  return (
    <div className={styles.card}>
      {status === "non_sellable" && (
        <div className={styles.non_sellable}>
          <Image
            src={`${assetLoader()}/icons/exclamation_light.svg`}
            alt="not available"
            width={12}
            height={12}
            loading="lazy"
          />
          <Text>Unavailable on ACKO Drive</Text>
        </div>
      )}

      {status === "out_of_production" && (
        <div className={styles.discontinued}>
          <Image
            src={`${assetLoader()}/icons/close_circular_light.svg`}
            alt="discontinued"
            width={12}
            height={12}
            loading="lazy"
          />
          <Text>Discontinued</Text>
        </div>
      )}

      {status === "upcoming" && (
        <div className={styles.upcoming}>
          <Image
            src={`${assetLoader()}/icons/close_circular_light.svg`}
            alt="discontinued"
            width={12}
            height={12}
            loading="lazy"
          />
          <Text>{BUYCARCARD_CONFIG.upcoming.text(expected_date)}</Text>
        </div>
      )}

      {status !== "non_sellable" &&
        status !== "out_of_production" &&
        status !== "upcoming" &&
        min_waiting_period &&
        min_waiting_period > 0 && (
          <div className={styles.expressDelivery}>
            <Image
              src={`${assetLoader()}/icons/thunder.svg`}
              alt="thunder"
              width={12}
              height={12}
            />
            {isLoggedIn ? (
              <Text>
                {BUYCARCARD_CONFIG.expressDelivery.text(min_waiting_period)}
              </Text>
            ) : (
              <Text>Express Delivery</Text>
            )}
          </div>
        )}

      <div className={styles.carImageWrapper}>
        <CarGalleryModal
          brand_name={brand_name}
          model_name={model_name}
          name={model_name}
          gallery={gallery || []}
          modalOverlayClassName={styles.modalOverlay}
          handleRouteClick={handleRouteClick}
          cityName={cityName}
          status={status}
          pageName={pageName}
        />

        <div className={styles.bottomGradientOverlay} />

        {experts_rating && (
          <div className={styles.ratingBadge}>
            <div className={styles.ratingImgWrapper}>
              <Image
                src={`${assetLoader()}/icons/RatingStar.svg`}
                alt="rating star"
                width={12}
                height={12}
                loading="eager"
              />
              <Text className={styles.ratingVal}>
                {parseFloat(experts_rating).toFixed(1)}/10
              </Text>
            </div>

            <div className={styles.ratingText}>Expert rating</div>
          </div>
        )}

        {trust_markers &&
          getHighestPriorityTrustMarkerCollection(trust_markers) &&
          !isCurrentPageTrustMarkerCollection() && (
            <div className={styles.trustMarkerBadgeWrapper}>
              <CollectionTrustMarkerBadge
                label={getHighestPriorityTrustMarkerCollection(trust_markers)!}
              />
            </div>
          )}

        {trust_markers?.award_trustmarkers &&
          trust_markers?.award_trustmarkers?.length > 0 && (
            <div className={styles.awardLogosWrapper}>
              {trust_markers?.award_trustmarkers
                ?.slice(0, 3)
                ?.map((award, index) => (
                  <div
                    key={`${award.tag_id}-${index}`}
                    className={styles.awardLogo}
                  >
                    <Image
                      src={award.logo_url}
                      alt={award.tag_name}
                      width={40}
                      height={40}
                    />
                  </div>
                ))}
            </div>
          )}
      </div>

      <CardContent
        brand_name={brand_name}
        model_name={model_name}
        model_slug={model_slug}
        experts_rating={experts_rating}
        min_price={min_price}
        max_price={max_price}
        min_on_road_price={min_on_road_price}
        min_acko_drive_savings={min_acko_drive_savings}
        min_waiting_period={min_waiting_period}
        min_emi_amount={min_emi_amount}
        variant_count={variant_count}
        status={status}
        fuel_types={fuel_types}
        transmission_types={transmission_types}
        isLoggedIn={isLoggedIn}
        isInWishlist={isWishlisted}
        cityName={cityName}
        collectionName={collectionName}
        handleSpinnerLoading={handleSpinnerLoading}
        onWishlistClick={onWishlistClick}
        handleRouteClick={handleRouteClick}
        title={title}
      />
    </div>
  );
};

export default BuyCarCard;
